import { PackingList, PackingListDetail } from './packingListApi';
import { gatewayApi as api } from './gatewayApi';

export interface CarboneConfig {
  templateId: string;
  carboneId: string;
  apiUrl: string;
}

export interface PackingListPrintData {
  packingList: PackingList;
  details: PackingListDetail[];
  companyLogo?: string;
}

/**
 * PDF Export Service for Packing Lists
 * Supports both browser print and Carbone.io professional template generation
 */
export class PDFExportService {
  private static carboneConfig: CarboneConfig | null = null;

  /**
   * Initialize Carbone configuration from backend
   */
  static async initializeCarboneConfig(): Promise<void> {
    try {
      const config = await api.get('/appconfig/carbone_data') as {
        template_id_packing_list: string;
        carbone_id: string;
      };
      this.carboneConfig = {
        templateId: config.template_id_packing_list,
        carboneId: config.carbone_id,
        apiUrl: 'https://render.carbone.io/render'
      };
    } catch (error) {
      console.warn('Failed to load Carbone configuration:', error);
      this.carboneConfig = null;
    }
  }

  /**
   * Generate professional PDF using Carbone.io template service
   */
  static async generateProfessionalPDF(data: PackingListPrintData): Promise<string | null> {
    if (!this.carboneConfig) {
      await this.initializeCarboneConfig();
      if (!this.carboneConfig) {
        throw new Error('Carbone configuration not available');
      }
    }

    try {
      // Prepare data in the format expected by the Carbone template
      const reportData = this.formatDataForCarbone(data);
      
      // Step 1: Send data to Carbone and get render ID
      const renderResponse = await fetch(
        `${this.carboneConfig.apiUrl}/${this.carboneConfig.templateId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.carboneConfig.carboneId}`,
            'Content-Type': 'application/json',
            'Carbone-Version': '3'
          },
          body: JSON.stringify({
            convertTo: 'pdf',
            data: reportData
          })
        }
      );

      const renderResult = await renderResponse.json();

      if (renderResult.success && renderResult.data?.renderId) {
        // Step 2: Get the generated PDF using the render ID
        const pdfUrl = `${this.carboneConfig.apiUrl}/${renderResult.data.renderId}`;
        return pdfUrl;
      } else {
        throw new Error(renderResult.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Professional PDF generation failed:', error);
      throw error;
    }
  }

  /**
   * Download PDF using Carbone.io and trigger browser download
   */
  static async downloadProfessionalPDF(data: PackingListPrintData, filename?: string): Promise<void> {
    try {
      const pdfUrl = await this.generateProfessionalPDF(data);
      if (!pdfUrl) {
        throw new Error('Failed to generate PDF URL');
      }

      // Download the PDF
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || `PackingList_${data.packingList.PackingListNum}_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('PDF download failed:', error);
      throw error;
    }
  }

  /**
   * Generate simple PDF using browser print functionality
   */
  static generateSimplePDF(data: PackingListPrintData): void {
    // Create a hidden print container
    const printContainer = document.createElement('div');
    printContainer.id = 'packing-list-print-container';
    printContainer.style.display = 'none';
    printContainer.innerHTML = this.generatePrintHTML(data);
    
    // Add to document
    document.body.appendChild(printContainer);
    
    // Show print container temporarily
    printContainer.style.display = 'block';
    
    // Print
    window.print();
    
    // Hide and clean up after delay
    setTimeout(() => {
      printContainer.style.display = 'none';
      document.body.removeChild(printContainer);
    }, 1000);
  }

  /**
   * Format data for Carbone template (matches Angular implementation)
   */
  private static formatDataForCarbone(data: PackingListPrintData): any {
    const { packingList, details } = data;
    
    return {
      PackingListData: {
        // Basic Information
        PackNumber: packingList.PackingListNum,
        PackingListName: packingList.PackingListName,
        Number: packingList.Number, // Customer PO
        RevDate: packingList.RevDate,
        
        // Shipping Information
        ShipFrom: packingList.ShipFrom,
        ShipFromCo: packingList.ShipFromCo,
        ShipFromSt: packingList.ShipFromSt,
        ShipFromCity: packingList.ShipFromCity,
        ShipFromState: packingList.ShipFromState,
        ShipFromZIP: packingList.ShipFromZIP,
        ShipFromPhone: packingList.ShipFromPhone,
        ShipFromEmail: packingList.ShipFromEmail,
        
        ShipTo: packingList.ShipTo,
        ShipToCo: packingList.ShipToCo,
        ShipToSt: packingList.ShipToSt,
        ShipToCity: packingList.ShipToCity,
        ShipToState: packingList.ShipToState,
        ShipToZIP: packingList.ShipToZIP,
        ShipToPhone: packingList.ShipToPhone,
        ShipToEmail: packingList.ShipToEmail,
        
        BillTo: packingList.BillTo,
        BillToCo: packingList.BillToCo,
        BillToSt: packingList.BillToSt,
        BillToCity: packingList.BillToCity,
        BillToState: packingList.BillToState,
        BillToZIP: packingList.BillToZIP,
        BillToPhone: packingList.BillToPhone,
        BillToEmail: packingList.BillToEmail,
        
        // Export Information
        CountryOrigin: packingList.CountryOrigin,
        USSchedBCode: packingList.USSchedBCode,
        ECCN: packingList.ECCN,
        ExportLicense: packingList.ExportLicense,
        TypeContainer: packingList.TypeContainer,
        
        // Measurements
        NetWeight: packingList.NetWeight,
        GrossWeight: packingList.GrossWeight,
        Volume: packingList.Volume,
        TotalShippedQty: packingList.TotalShippedQty,
        TotalContainer: packingList.TotalContainer,
        
        // Reference
        RefDocName: packingList.RefDocName,
        
        // Product Details
        SelectedOptions: details.map(detail => ({
          OriginSerialNum: detail.Product?.Number || detail.ProductId,
          Product: {
            Description: detail.Description || detail.Product?.Description || 'N/A',
            UoMId: detail.Product?.UnitOfMeasure || 'EA'
          },
          PurchaseOrder: {
            Number: packingList.Number, // Customer PO
            OrderQty: detail.Quantity
          },
          WorkOrder: {
            WONum: 'N/A', // Would need to be linked if available
            Qty: detail.Quantity
          },
          LineNum: detail.id || '1',
          Quantity: detail.Quantity,
          UnitPrice: detail.UnitPrice,
          TotalPrice: detail.TotalPrice
        }))
      }
    };
  }

  /**
   * Generate HTML for simple browser print (matches Angular template structure)
   */
  private static generatePrintHTML(data: PackingListPrintData): string {
    const { packingList, details, companyLogo } = data;
    
    const logoHtml = companyLogo 
      ? `<img src="${companyLogo}" alt="Company Logo" style="max-height: 80px; margin-bottom: 20px;" />`
      : '';
    
    const detailsHtml = details.map(detail => `
      <tr>
        <td>${detail.Product?.Number || detail.ProductId}</td>
        <td>${detail.Description || detail.Product?.Description || 'N/A'}</td>
        <td>${packingList.Number}</td>
        <td>N/A</td>
        <td>${detail.id || '1'}</td>
        <td>${detail.Quantity}</td>
        <td>${detail.Quantity}</td>
        <td>${detail.Product?.UnitOfMeasure || 'EA'}</td>
      </tr>
    `).join('');

    return `
      <div style="font-family: Arial, sans-serif; max-width: 1200px; margin: 0 auto; padding: 20px;">
        ${logoHtml}
        
        <table style="width: 100%; margin-bottom: 30px;">
          <tr>
            <td style="width: 33%;">
              <h3>Pack Slip Number: <i>${packingList.PackingListNum}</i></h3>
            </td>
            <td style="width: 33%;"></td>
            <td style="width: 33%;"></td>
          </tr>
          <tr>
            <td><h3>Ship From</h3></td>
            <td><h3>Ship To</h3></td>
            <td><h3>Bill To</h3></td>
          </tr>
          <tr>
            <td style="vertical-align: top;">
              <h4><b>Name:</b> <i>${packingList.ShipFrom || ''}</i></h4>
              <h4><b>Company:</b> <i>${packingList.ShipFromCo || ''}</i></h4>
              <h4><b>Street:</b> <i>${packingList.ShipFromSt || ''}</i></h4>
              <h4><b>City:</b> <i>${packingList.ShipFromCity || ''}</i></h4>
              ${packingList.ShipFromState ? `<h4><b>State:</b> <i>${packingList.ShipFromState}</i></h4>` : ''}
              ${packingList.ShipFromZIP ? `<h4><b>Zip Code:</b> <i>${packingList.ShipFromZIP}</i></h4>` : ''}
              ${packingList.ShipFromPhone ? `<h4><b>Phone:</b> <i>${packingList.ShipFromPhone}</i></h4>` : ''}
              ${packingList.ShipFromEmail ? `<h4><b>Email:</b> <i>${packingList.ShipFromEmail}</i></h4>` : ''}
            </td>
            <td style="vertical-align: top;">
              <h4><b>Name:</b> <i>${packingList.ShipTo || ''}</i></h4>
              <h4><b>Company:</b> <i>${packingList.ShipToCo || ''}</i></h4>
              <h4><b>Street:</b> <i>${packingList.ShipToSt || ''}</i></h4>
              <h4><b>City:</b> <i>${packingList.ShipToCity || ''}</i></h4>
              <h4><b>State:</b> <i>${packingList.ShipToState || ''}</i></h4>
              <h4><b>Zip Code:</b> <i>${packingList.ShipToZIP || ''}</i></h4>
              <h4><b>Phone:</b> <i>${packingList.ShipToPhone || ''}</i></h4>
              <h4><b>Email:</b> <i>${packingList.ShipToEmail || ''}</i></h4>
            </td>
            <td style="vertical-align: top;">
              <h4><b>Name:</b> <i>${packingList.BillTo || ''}</i></h4>
              <h4><b>Company:</b> <i>${packingList.BillToCo || ''}</i></h4>
              <h4><b>Street:</b> <i>${packingList.BillToSt || ''}</i></h4>
              <h4><b>City:</b> <i>${packingList.BillToCity || ''}</i></h4>
              <h4><b>State:</b> <i>${packingList.BillToState || ''}</i></h4>
              <h4><b>Zip Code:</b> <i>${packingList.BillToZIP || ''}</i></h4>
              <h4><b>Phone:</b> <i>${packingList.BillToPhone || ''}</i></h4>
              <h4><b>Email:</b> <i>${packingList.BillToEmail || ''}</i></h4>
            </td>
          </tr>
        </table>

        <hr style="margin: 30px 0;" />

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <tr>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">Item</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">Description</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">PO Number</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">Work Order</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">Line</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">Qty Req</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">Qty Ship</td>
            <td style="font-weight: bold; border: 1px solid #ddd; padding: 8px;">UOM</td>
          </tr>
          ${detailsHtml}
        </table>

        <hr style="margin: 30px 0;" />

        <table style="width: 100%; margin-top: 30px;">
          <tr>
            <td style="width: 50%;">
              <h4><b>Country of Origin:</b> ${packingList.CountryOrigin || 'N/A'}</h4>
              <h4><b>US Schedule B Code:</b> ${packingList.USSchedBCode || 'N/A'}</h4>
              <h4><b>ECCN:</b> ${packingList.ECCN || 'N/A'}</h4>
              <h4><b>Export License:</b> ${packingList.ExportLicense || 'N/A'}</h4>
            </td>
            <td style="width: 50%;">
              <h4><b>Net Weight:</b> ${packingList.NetWeight || 0} lb</h4>
              <h4><b>Gross Weight:</b> ${packingList.GrossWeight || 0} lb</h4>
              <h4><b>Volume:</b> ${packingList.Volume || 0} L</h4>
              <h4><b>Total Containers:</b> ${packingList.TotalContainer || 0}</h4>
            </td>
          </tr>
        </table>

        <div style="margin-top: 40px; text-align: center;">
          <p><b>Reference Document:</b> ${packingList.RefDocName || 'N/A'}</p>
          <p><small>Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</small></p>
        </div>
      </div>

      <style>
        @media print {
          body * {
            visibility: hidden;
          }
          #packing-list-print-container,
          #packing-list-print-container * {
            visibility: visible;
          }
          #packing-list-print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page {
            margin: 1in;
            size: letter;
          }
        }
      </style>
    `;
  }

  /**
   * Get PDF export options for user selection
   */
  static getExportOptions(): Array<{
    id: string;
    name: string;
    description: string;
    available: boolean;
  }> {
    return [
      {
        id: 'simple',
        name: 'Simple Print',
        description: 'Quick browser-based printing',
        available: true
      },
      {
        id: 'professional',
        name: 'Professional PDF',
        description: 'High-quality PDF with company template',
        available: this.carboneConfig !== null
      }
    ];
  }

  /**
   * Auto-detect best export method
   */
  static async getRecommendedExportMethod(): Promise<'simple' | 'professional'> {
    if (!this.carboneConfig) {
      await this.initializeCarboneConfig().catch(() => {});
    }
    return this.carboneConfig ? 'professional' : 'simple';
  }
}

// Auto-initialize when service is imported
PDFExportService.initializeCarboneConfig().catch(console.warn); 